// src/services/api/applications.ts
import { supabase } from './database/supabase';
import { handleApiError, useError } from '@/shared/contexts/ErrorContext';
import type { FilterOptions } from '@/types/api';
import type { Application, Location } from '@/types/models/schema';

// Define the application input type to match schema requirements
export interface ApplicationInput {
  applicant_name: string;
  applicant_email: string;
  company?: string | undefined;
  title?: string | undefined;
  bio?: string | undefined;
  location?: Location | undefined;
  status: 'pending' | 'approved' | 'rejected';
}

// Create a mock addError function for service layer
// In a real implementation, this would be passed from the component using the service
const mockAddError = (error: Parameters<ReturnType<typeof useError>['addError']>[0]) => {
  console.error('API Error:', error);
};

export const applicationService = {
  // Get all applications with filtering
  getAll: async (filters?: FilterOptions): Promise<Application[] | null> => {
    try {
      let query = supabase.from('applications').select('*');
      
      // Apply filters if provided
      if (filters) {
        if (filters.status) {
          query = query.eq('status', filters.status);
        }
        
        if (filters.search) {
          query = query.or(`applicant_name.ilike.%${filters.search}%,applicant_email.ilike.%${filters.search}%,company.ilike.%${filters.search}%`);
        }
        
        // Pagination
        if (filters.limit) {
          query = query.limit(filters.limit);
        }
        
        if (filters.page && filters.limit) {
          const offset = (filters.page - 1) * filters.limit;
          query = query.range(offset, offset + filters.limit - 1);
        }
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return data as Application[];
    } catch (error) {
      handleApiError(error, mockAddError);
      return null;
    }
  },
  
  // Get application by ID
  getById: async (id: string): Promise<Application | null> => {
    try {
      const { data, error } = await supabase
        .from('applications')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      
      return data as Application;
    } catch (error) {
      handleApiError(error, mockAddError);
      return null;
    }
  },
  
  // Create application
  create: async (data: ApplicationInput): Promise<Application | null> => {
    try {
      const { data: newApplication, error } = await supabase
        .from('applications')
        .insert([{ ...data, created_at: new Date().toISOString() }])
        .select()
        .single();
      
      if (error) throw error;
      
      return newApplication as Application;
    } catch (error) {
      handleApiError(error, mockAddError);
      return null;
    }
  },
  
  // Update application
  update: async (id: string, data: Partial<ApplicationInput>): Promise<Application | null> => {
    try {
      const { data: updatedApplication, error } = await supabase
        .from('applications')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      return updatedApplication as Application;
    } catch (error) {
      handleApiError(error, mockAddError);
      return null;
    }
  },
  
  // Delete application
  delete: async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('applications')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      return true;
    } catch (error) {
      handleApiError(error, mockAddError);
      return false;
    }
  },
  
  // Convert application to guest
  convertToGuest: async (id: string): Promise<boolean> => {
    try {
      // First get the application
      const { data: application, error: fetchError } = await supabase
        .from('applications')
        .select('*')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Create a new guest from the application data
      const { error: createError } = await supabase
        .from('guests')
        .insert([{
          name: application.applicant_name,
          email: application.applicant_email,
          company: application.company,
          title: application.title,
          bio: application.bio,
          status: 'pending',
          location: application.location,
          created_at: new Date().toISOString()
        }]);
      
      if (createError) throw createError;
      
      // Update the application status to converted
      const { error: updateError } = await supabase
        .from('applications')
        .update({ 
          status: 'approved'
        })
        .eq('id', id);
      
      if (updateError) throw updateError;
      
      return true;
    } catch (error) {
      handleApiError(error, mockAddError);
      return false;
    }
  }
};

export default applicationService;
